import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { FundDetailsService } from '../../services/fund-details/fund-details';
import { FundOffering } from '../../services/fund-offering/fund-offering';
import { FundStatus, OfferingStatus } from '../../enums/fund-status';
import { ROUTE_PATHS } from '../../utils/route-paths.const';
import { FUND_DETAILS_TABS } from '../../constants/tab-names.const';

export const offerGuard: CanActivateFn = (route): Observable<boolean> => {
  const fundDetailsService = inject(FundDetailsService);
  const fundOfferingService = inject(FundOffering);
  const router = inject(Router);
  const fundUuid = route.paramMap.get('fundUuid');
  const offerUuid = route.queryParamMap.get('offerUuid');
  const routePath = route.routeConfig?.path || '';

  // Determine if this is an initial offer guard based on route path
  const isInitialOffer = routePath === ROUTE_PATHS.INITIAL_OFFER_CREATION;

  // Helper function to navigate to main-details with offerings tab
  const navigateToOfferingsTab = (): void => {
    router.navigate(
      [ROUTE_PATHS.FUND_MANAGEMENT, fundUuid, ROUTE_PATHS.DETAILS],
      {
        queryParams: { tab: FUND_DETAILS_TABS.OFFERING },
      }
    );
  };

  if (!fundUuid) {
    router.navigate([ROUTE_PATHS.ROOT]);
    return of(false);
  }

  // Check if navigation is from main-details (bypass guard in this case)
  const navigation = router.currentNavigation();
  const bypassGuard = navigation?.extras?.state?.['bypassGuard'];

  if (bypassGuard) {
    return of(true);
  }

  return fundDetailsService.getFundDetails(fundUuid).pipe(
    switchMap(fundDetails => {
      if (fundDetails.state?.value !== FundStatus.APPROVED) {
        router.navigate([
          ROUTE_PATHS.FUND_MANAGEMENT,
          fundUuid,
          ROUTE_PATHS.DETAILS,
        ]);
        return of(false);
      }

      // Initial offer: redirect if has_completed_offerings is true
      // Regular offer: redirect if has_completed_offerings is false
      if (isInitialOffer) {
        if (fundDetails.has_completed_offerings) {
          navigateToOfferingsTab();
          return of(false);
        }
      } else {
        // TODO: re-enable secondary offerings when supported
        // if (!fundDetails.has_completed_offerings) {
        //   navigateToOfferingsTab();
        //   return of(false);
        // }
        navigateToOfferingsTab();
        return of(false);
      }

      const latestOfferState = fundDetails.offering?.state?.value;

      // If offerUuid is present, fetch offering details and validate state
      if (offerUuid) {
        return fundOfferingService.getOfferingDetails(fundUuid, offerUuid).pipe(
          map(offeringResponse => {
            const offeringState = offeringResponse.data.state?.value;
            const offeringIsInitial = offeringResponse.data.is_initial;

            // Validate that is_initial matches the route type
            if (offeringIsInitial !== isInitialOffer) {
              navigateToOfferingsTab();
              return false;
            }

            if (offeringState !== OfferingStatus.PENDING_CLARIFICATION) {
              navigateToOfferingsTab();
              return false;
            }

            return true;
          }),
          catchError(() => {
            navigateToOfferingsTab();
            return of(false);
          })
        );
      } else {
        // If no offerUuid, validate that latest_offer_state is null, COMPLETED, or REJECTED
        if (
          latestOfferState !== undefined &&
          latestOfferState !== OfferingStatus.COMPLETED &&
          latestOfferState !== OfferingStatus.REJECTED
        ) {
          navigateToOfferingsTab();
          return of(false);
        }

        return of(true);
      }
    }),
    catchError(() => {
      router.navigate([ROUTE_PATHS.ROOT]);
      return of(false);
    })
  );
};
